import { createFileRoute, Navigate } from '@tanstack/react-router';

import { RunGroupList } from '@apps/web/features/teach_phoebe/ui/run_group_list';
import { getRunEnvironment } from '@apps/web/lib/runtime_config';
import { type TrainingRunGroup } from '@apps/web/lib/training_run_utils';
import { type TrainingRunRead } from '@libraries/typescript/api_client';

export const Route = createFileRoute('/teach-phoebe-feedback-tags-mockup')({
    component: TeachPhoebeFeedbackTagsMockupRoute,
    head: () => ({
        meta: [
            {
                title: 'Teach Phoebe feedback tags mockup | Phoebe',
            },
        ],
    }),
});

// Renders the real RunGroupList/RunRow components (not a hand-copied
// approximation) against mock data, so the tag size shown here always
// matches apps/web/features/teach_phoebe/ui/run_row.tsx exactly.
// organizationId is null, which makes RunRow's thumb handlers no-op
// (see handleThumb's early return), so no network calls fire.
function mockRun(overrides: Partial<TrainingRunRead>): TrainingRunRead {
    return {
        id: overrides.id ?? 'mock-run',
        channel: 'chat',
        title: 'Mock run',
        description: null,
        summary: null,
        run_type: 'general',
        activity_type: 'chat',
        status: 'completed',
        created_at: new Date().toISOString(),
        last_activity_at: new Date().toISOString(),
        feedback_status: null,
        feedback_label: null,
        created_by_user_id: null,
        created_by_display_name: null,
        ...overrides,
    };
}

const MOCK_GROUP: TrainingRunGroup = {
    key: 'phoebe-team-user',
    label: 'Phoebe Team User',
    isPhoebe: false,
    isOwn: true,
    runs: [
        mockRun({
            id: 'chat-1',
            title: "Today's schedule overview and status",
            description:
                'Caregiver: I can cover Saturday morning if the address is nearby.',
            channel: 'chat',
            run_type: 'general',
        }),
        mockRun({
            id: 'chat-2',
            title: 'Add contact to outreach exclusion list',
            channel: 'chat',
            run_type: 'general',
        }),
        mockRun({
            id: 'chat-3',
            title: 'Outreach campaign setup request',
            channel: 'chat',
            run_type: 'outreach_analysis',
        }),
        mockRun({
            id: 'auto-1',
            title: 'Auto shift reminder batch',
            channel: 'chat',
            run_type: 'automation',
            activity_type: 'workflow',
        }),
        mockRun({
            id: 'voice-1',
            title: 'Inbound call from unknown number',
            channel: 'voice',
        }),
        mockRun({
            id: 'sms-1',
            title: 'Shift confirmation reply thread',
            channel: 'sms',
        }),
    ],
};

function TeachPhoebeFeedbackTagsMockupRoute() {
    if (getRunEnvironment() !== 'development') {
        return <Navigate to="/teach-phoebe" replace={true} />;
    }

    return (
        <main className="bg-background min-h-full">
            <div className="mx-auto flex w-full max-w-4xl flex-col gap-4 px-6 py-8">
                <header className="space-y-1.5 border-b pb-5">
                    <h1 className="text-foreground text-2xl font-semibold">
                        Teach Phoebe — Needs review summaries
                    </h1>
                    <p className="text-muted-foreground max-w-2xl text-sm">
                        Real RunGroupList/RunRow components with mock data. The
                        first row includes an expandable run summary from{' '}
                        <code>
                            apps/web/features/teach_phoebe/ui/run_row.tsx
                        </code>{' '}
                        so the review list stays compact until a coordinator
                        asks for the concrete interaction detail.
                    </p>
                </header>
                <div className="bg-card min-w-0 self-start rounded-xl border shadow-sm">
                    <RunGroupList
                        groups={[MOCK_GROUP]}
                        organizationId={null}
                        onFeedbackSaved={() => undefined}
                        onReviewingChange={() => undefined}
                    />
                </div>
            </div>
        </main>
    );
}
